/**
 * Página principal do Dashboard do Tenant
 *
 * Exibe dashboards em formato de abas (Conversas Geral, Atendimento).
 * Usa o sistema centralizado de dashboards via useModuleDashboards.
 *
 * @author Sistema
 * @version 2.1.0
 * @release 2026-07-02 - Regra unificada de trial (legado V1 + Planos 2.0):
 *          WelcomeCard agora usa tenants.status='trial' + trial_ends_at,
 *          sem depender do slug do plano ('free' / 'free_v1'). Respeita
 *          o campo "Plano padrão de novos tenants" gerenciado em
 *          subscription_plans e mantém compatibilidade com tenants legados.
 * @release 2026-04-06 - Migrado para sistema de abas com DashboardTabs
 */

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useModuleDashboards } from '@/hooks/useDashboards';
import { DashboardTabs } from '@/components/crm/dashboard/DashboardTabs';
import { DashboardRenderer } from '@/components/crm/dashboard/DashboardRegistry';
import { WelcomeCard } from '@/components/tenant/WelcomeCard';
import { toast } from 'sonner';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import { CrmInboxFilter } from '@/components/crm/filters/CrmInboxFilter';
import { CrmInboxGroupFilter } from '@/components/crm/filters/CrmInboxGroupFilter';
import { useInboxIdsByGroup } from '@/hooks/useInboxGroups';
import { calculatePeriodDates, type PeriodFilter, type PeriodPreset } from '@/hooks/useCrmOverviewFilters';

/**
 * Dashboard do Tenant com sistema de abas para módulo Conversas
 */
const TenantDashboard = () => {
  const { currentTenant, isFirstAccess, clearFirstAccess, profile } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);

  // Estado dos filtros (levantado para o header)
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>(() => {
    // Default alterado para "Hoje" — RPCs do dashboard aceitam days_ago=1 nativamente
    const dates = calculatePeriodDates('today');
    return { preset: 'today', ...dates };
  });
  const [inboxId, setInboxId] = useState<string | null>(null);
  // Grupo selecionado — resolve para uma lista de inbox_ids no backend
  const [groupId, setGroupId] = useState<string | null>(null);

  // Resolve IDs de inbox vinculadas ao grupo selecionado
  const { data: groupInboxIds } = useInboxIdsByGroup(groupId);

  // Quando o grupo muda: se a inbox selecionada não pertence ao grupo, limpa-a automaticamente
  useEffect(() => {
    if (!groupId || !inboxId) return;
    if (groupInboxIds && groupInboxIds.length > 0 && !groupInboxIds.includes(inboxId)) {
      setInboxId(null);
    }
  }, [groupId, groupInboxIds, inboxId]);

  // IDs permitidos no filtro de inbox quando há um grupo selecionado.
  // null = sem restrição; [] = grupo sem inboxes vinculadas (lista vazia).
  const allowedInboxIds = groupId ? (groupInboxIds ?? []) : null;

  // Handlers de período
  const handlePeriodPresetChange = useCallback((preset: PeriodPreset) => {
    const dates = calculatePeriodDates(preset);
    setPeriodFilter({ preset, ...dates });
  }, []);

  const handleCustomPeriodChange = useCallback((startDate: Date, endDate: Date) => {
    setPeriodFilter({ preset: 'custom', startDate, endDate });
  }, []);


  // Busca dashboards do módulo "conversations"
  const {
    dashboards,
    selectedDashboard,
    selectedDashboardId,
    selectDashboard,
    isLoading: isLoadingDashboards,
  } = useModuleDashboards('conversations');

  // O filtro de Grupos só faz sentido no dashboard "Conversas Geral"
  // (component_key === 'conversation_overview'); nas demais abas é ocultado
  // e o estado é resetado para evitar filtro "preso" invisível.
  const showGroupFilter = selectedDashboard?.component_key === 'conversation_overview';

  useEffect(() => {
    if (!showGroupFilter && groupId !== null) {
      setGroupId(null);
    }
  }, [showGroupFilter, groupId]);

  // v1.3.0 — Regra unificada de trial (legado V1 + Planos 2.0):
  // baseada em tenants.status='trial' + trial_ends_at futuro.
  // Não depende mais do slug do plano ('free' / 'free_v1'), respeitando
  // o campo "Plano padrão de novos tenants" gerenciado em
  // subscription_plans. Mantém compatibilidade total com tenants legados
  // (que também usam status='trial' + trial_ends_at) e fica consistente
  // com o TenantBillingBanner ("Período de teste ativo · N dias").
  const isInTrialPeriod =
    currentTenant?.status === 'trial' &&
    !!currentTenant?.trial_ends_at &&
    new Date(currentTenant.trial_ends_at).getTime() > Date.now();

  // Exibe WelcomeCard durante trial (só decide após resolver o plano efetivo)
  useEffect(() => {
    setShowWelcome(isInTrialPeriod);
  }, [isInTrialPeriod]);

  // Toast de boas-vindas no primeiro acesso
  useEffect(() => {
    if (isFirstAccess && currentTenant) {
      const timer = setTimeout(() => {
        toast.success(
          `Bem-vindo ao ${currentTenant.name}!`,
          {
            description: 'Seu workspace foi criado com sucesso. Siga os passos para começar.',
            duration: 5000,
          }
        );
        clearFirstAccess();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isFirstAccess, currentTenant, clearFirstAccess]);

  // Handler para fechar card de boas-vindas
  const handleDismissWelcome = () => {
    setShowWelcome(false);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Card de boas-vindas (trial) */}
      {showWelcome && (
        <WelcomeCard onDismiss={handleDismissWelcome} />
      )}

      {/* Header compacto - padrão Contatos */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">
            Bem-vindo, {profile?.full_name?.split(' ')[0] || currentTenant?.name || 'Cliente'}!
          </h1>
          <p className="text-xs text-muted-foreground">
            Acompanhe os indicadores e métricas do seu negócio
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {showGroupFilter && (
            <CrmInboxGroupFilter
              value={groupId}
              onChange={setGroupId}
              className="h-[22px] text-xs px-2"
            />
          )}
          <CrmInboxFilter
            value={inboxId}
            onChange={setInboxId}
            allowedIds={allowedInboxIds}
            className="h-[22px] text-xs px-2"
          />
          <CrmPeriodFilter
            value={periodFilter}
            onPresetChange={handlePeriodPresetChange}
            onCustomChange={handleCustomPeriodChange}
            className="h-[22px] text-xs px-2"
          />
        </div>
      </div>

      <div className="space-y-3">

        {/* Abas de dashboards */}
        <DashboardTabs
          dashboards={dashboards}
          selectedDashboardId={selectedDashboardId}
          onSelectDashboard={selectDashboard}
          isLoading={isLoadingDashboards}
        />
      </div>

      {/* Conteúdo do dashboard selecionado */}
      {selectedDashboard && (
        <DashboardRenderer
          componentKey={selectedDashboard.component_key}
          dashboardId={selectedDashboard.id}
          conversationFilters={{
            periodFilter,
            inboxId,
            // Quando há grupo selecionado, envia a lista de inboxes vinculadas (pode ser vazia)
            inboxIds: groupId ? (groupInboxIds ?? []) : null,
          }}
        />
      )}
    </div>
  );
};

export default TenantDashboard;

